import { useEffect, useState } from "react";
import Markdown from "react-markdown";
import styled from "styled-components";

import ErrorMessageReporting from "../ErrorMessageReporting";
import FlexBox from "../FlexBox";
import AiChatMessageBubble from "./AiChatMessageBubble";
import AiChatMessageDateDivider from "./AiChatMessageDateDivider";
import AiChatMessageFooter from "./AiChatMessageFooter";
import AiChatTypingText from "./AiChatTypingText";
import {
  formatMessageDateLabel,
  getMessageDateKey,
  getMessageId,
  shouldShowAiMessageFooter,
  shouldShowMessageDateDivider,
} from "./aiChatHelpers";

const MessageItem = styled(FlexBox)`
  width: 100%;
  flex-direction: column;
  align-items: stretch;
`;

const MarkdownContent = styled.div`
  width: 100%;
`;

const AiChatMessageItem = ({
  message,
  index,
  messages,
  openMenuId,
  completedTyping,
  menuRefs,
  onCopy,
  onRegenerate,
  onLike,
  onDislike,
  onToggleMenu,
  onReport,
  onTypingComplete,
  displayed,
  setDisplayed,
  displayedLengthRef,
  intervalRef,
}) => {
  const [showSteps, setShowSteps] = useState(true);
  const [showMessageBubble, setShowMessageBubble] = useState(false);

  useEffect(() => {
    if (message?.markdownText) {
      setShowSteps(false);
      setShowMessageBubble(true);
    }
  }, [message?.markdownText]);

  const messageId = getMessageId(message, index);

  const isAi = message?.sender === "ai";
  const isReported = !!message?.isReported;

  const currentDateKey = getMessageDateKey(message);
  const previousDateKey =
    index > 0 ? getMessageDateKey(messages[index - 1]) : null;

  const showDateDivider = shouldShowMessageDateDivider(
    currentDateKey,
    previousDateKey
  );

  const lastMessage = messages[messages.length - 1];
  const isLastMessage = index === messages.length - 1;
  const lastMessageIsAi = lastMessage?.sender === "ai";

  const isTypingComplete = completedTyping[messageId];

  const showFooter = shouldShowAiMessageFooter({
    isAi,
    isLastMessage,
    isStreaming: message?.isStreaming,
    isTypingComplete,
  });

  return (
    <MessageItem>
      {showDateDivider && (
        <AiChatMessageDateDivider
          label={formatMessageDateLabel(currentDateKey)}
        />
      )}

      {(message?.markdownText || message?.toolEvents?.length > 0) &&
        !message?.errorInfo && (
          <AiChatMessageBubble
            sender={message?.sender}
            clientName={message?.clientName}
            clientCid={message?.clientCid}
            toolEvents={message?.toolEvents || []}
            showSteps={showSteps}
            setShowSteps={setShowSteps}
            showMessageBubble={showMessageBubble}
            footer={
              showFooter ? (
                <AiChatMessageFooter
                  message={message}
                  messageId={messageId}
                  isLastMessage={isLastMessage}
                  lastMessageIsAi={lastMessageIsAi}
                  isMenuOpen={openMenuId === messageId}
                  isReported={isReported}
                  menuRef={node => {
                    if (node) {
                      menuRefs.current[messageId] = node;
                    } else {
                      delete menuRefs.current[messageId];
                    }
                  }}
                  onCopy={onCopy}
                  onRegenerate={onRegenerate}
                  onLike={onLike}
                  onDislike={onDislike}
                  onToggleMenu={onToggleMenu}
                  onReport={onReport}
                />
              ) : null
            }
          >
            <MarkdownContent>
              {isLastMessage && isAi && message?.isStreaming ? (
                <AiChatTypingText
                  speed={25}
                  text={message?.markdownText || ""}
                  onComplete={() => onTypingComplete(messageId)}
                  displayed={displayed}
                  setDisplayed={setDisplayed}
                  displayedLengthRef={displayedLengthRef}
                  intervalRef={intervalRef}
                />
              ) : (
                <Markdown>{message?.markdownText || ""}</Markdown>
              )}
            </MarkdownContent>
          </AiChatMessageBubble>
        )}

      {message?.errorInfo && (
        <ErrorMessageReporting
          title={message.errorInfo.errorTitle}
          errorDescription={message.errorInfo.errorDescription}
          suggestionText={message.errorInfo.errorSuggestion}
          onReport={onReport}
          reportButtonText="Report"
          message={message}
          messageId={messageId}
          isReported={isReported}
        />
      )}
    </MessageItem>
  );
};

export default AiChatMessageItem;
